import { Skeleton } from '@/components/ui/skeleton'
import { useAsync } from 'react-use'
import client from '@/services/client.service'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import ContentPreview from './ContentPreview'
import UserAvatar from './UserAvatar'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { ExternalPointer } from '@nostr/tools/nip22'

export default function ParentNotePreview({
  parent,
  first,
  className,
  onClick
}: {
  parent: string | EventPointer | AddressPointer | ExternalPointer
  first: boolean
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined
}) {
  const { t } = useTranslation()
  const { value: event, loading: isFetching } = useAsync(async () => {
    if (!parent) return undefined
    if (typeof parent === 'object' && 'value' in parent) return undefined
    return client.fetchEvent(parent)
  }, [parent])

  if (typeof parent === 'object' && 'value' in parent) {
    return (
      <div
        className={cn(
          'flex gap-1 items-center text-sm rounded-full pl-2 pr-1 bg-muted text-muted-foreground min-w-0 max-w-full',
          event && 'hover:text-foreground cursor-pointer',
          className
        )}
        onClick={onClick}
      >
        {first && <div className="shrink-0">{t('reply to')}</div>}
        {event && <UserAvatar className="shrink-0" user={event.pubkey} size="tiny" />}
        <div className="truncate min-w-0">{parent.value}</div>
      </div>
    )
  }

  if (isFetching) {
    return (
      <div
        className={cn(
          'flex gap-1 items-center text-sm rounded-full pl-2 pr-6 bg-muted w-44 max-w-full text-muted-foreground',
          className
        )}
      >
        <div className="shrink-0">{t('reply to')}</div>
        <Skeleton className="w-4 h-4 rounded-full" />
        <div className="py-1 flex-1">
          <Skeleton className="h-3" />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex gap-1 items-center text-sm rounded-full px-2 bg-muted text-muted-foreground w-fit min-w-0 max-w-full',
        event && 'hover:text-foreground cursor-pointer',
        className
      )}
      onClick={event ? onClick : undefined}
    >
      {first && <div className="shrink-0">{t('reply to')}</div>}
      {event && <UserAvatar className="shrink-0" user={event.pubkey} size="tiny" />}
      <ContentPreview className="truncate min-w-0" event={event} />
    </div>
  )
}
